import { useCallback } from 'react';
import { useDispatch } from 'react-redux';

import { addReloadItemToCart } from 'common/api/e-comm/cart';
import { addItemToCart } from 'common/api/e-comm/cart/lineitem';
import { Product, ProductType } from 'common/api/e-comm/models/Product';
import { ProductAmountInputFormData } from 'features/product/components/ProductAmountInput';
import { ReloadableProductAmountFormData } from 'features/product/components/ReloadTabPanel';
import { defaultApiErrorMessage } from 'utils/apiResponseMessages';
import { requestCartItems } from 'common/features/store/duck/cart/duck';
import { useNavMenu } from 'components/nav/context';
import { useAlert } from 'modules/alerts';
import { gaLogAddToCart } from 'config/analytics';
import { UniversalBrand } from 'common/api/models/UniversalBrand';
import { CartResponse } from 'common/api/e-comm/cart/lineitem/models/CartItems';
import { APIResponse } from 'common/api/models';
import { CartLineItemGiftDetails } from 'common/api/e-comm/models/CartLineItem';

export const useAddProductToCart = () => {
    const dispatch = useDispatch<any>();
    const { setOpenMenu } = useNavMenu();
    const alert = useAlert();

    const addProductToCart = useCallback(
        async (
            product: Product,
            amount: ProductAmountInputFormData,
            brand?: UniversalBrand,
            giftDetail?: CartLineItemGiftDetails,
            showAddToCartAnimation = true
        ) => {
            let response: APIResponse<CartResponse, null>;

            if (product.type === ProductType.Reload) {
                const reloadableAmount = amount as ReloadableProductAmountFormData;
                response = await addReloadItemToCart({
                    productId: product.id,
                    cardId: reloadableAmount.cardId,
                    priceOverride: reloadableAmount.priceOverride,
                    quantity: reloadableAmount.quantity,
                    selectedOptions: reloadableAmount.selectedOptions,
                });

                if (response.error) {
                    alert(defaultApiErrorMessage());
                    return response;
                }
            } else {
                const hasOptions = Object.keys(amount.selectedOptions).length > 0;
                response = await addItemToCart({
                    productId: product.id,
                    priceOverride:
                        hasOptions || product.isLocalProduct ? null : amount.priceOverride,
                    quantity: amount.quantity,
                    selectedOptions: amount.selectedOptions,
                    giftDetail,
                });

                if (response.error) {
                    alert(defaultApiErrorMessage());
                    return response;
                }
            }

            gaLogAddToCart({
                product,
                brand,
                price: product.price,
                quantity: amount.quantity,
            });

            if (showAddToCartAnimation) {
                setOpenMenu('cart');
                await requestCartItems()(dispatch);
            }

            return response;
        },
        [alert, dispatch, setOpenMenu]
    );

    return { addProductToCart };
};
